/**
 * API client for productivity goals endpoints
 */

import { RateLimitError, TurnstileError, APIError } from './errors';

const API_URL = import.meta.env.VITE_API_URL || 'https://mybestday.ai/api';

interface AnalyzeGoalsRequest {
  goals: string[];
  turnstileToken?: string; // Optional - API uses IP rate limiting instead
}

interface ProductivityQuestion {
  id: string;
  text: string;
  type: 'text' | 'choice' | 'multiple' | 'scale';
  options?: string[];
}

interface AnalyzeGoalsResponse {
  questions: ProductivityQuestion[];
}

interface GenerateReportRequest {
  goals: string[];
  answers: Record<string, string>;
  email: string;
  consentMarketing: boolean;
  turnstileToken?: string; // Optional - API uses IP rate limiting instead
}

interface ProductivityReport {
  id: string;
  userId: string;
  generatedAt: string;
  goals: Array<{
    description: string;
    priority: number;
    reasoning: string;
    estimatedTimeToComplete: string;
    dependencies: string[];
  }>;
  questions: Array<{
    question: string;
    answer: string;
  }>;
  recommendations: {
    priorityOrder: string[];
    timeline: Array<{
      phase: string;
      duration: string;
      goals: string[];
      milestones: string[];
    }>;
    dailyHabits: string[];
    weeklyHabits: string[];
    nextActions: Record<string, string[]>;
    strategies: string[];
  };
  customAdvice: string;
}

interface GenerateReportResponse {
  report: ProductivityReport;
  userId: string;
}

interface ConvertToFullRequest {
  softUserId: string;
  googleCredential?: string;
  turnstileToken?: string; // Optional - API uses IP rate limiting instead
}

interface ConvertToFullResponse {
  fullUserId: string;
  message: string;
}

/**
 * Handle API errors and convert to custom error types
 */
async function handleResponse<T>(response: Response): Promise<T> {
  // Rate limit error
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After');
    const retrySeconds = retryAfter ? parseInt(retryAfter, 10) : 3600; // default 1 hour
    throw new RateLimitError(retrySeconds);
  }

  // Unauthorized/Forbidden
  if (response.status === 401 || response.status === 403) {
    throw new TurnstileError('Security verification failed. Please refresh and try again.');
  }

  // Other errors
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();

      // Extract error message, handling various response formats
      if (typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (typeof errorData.message === 'string') {
        errorMessage = errorData.message;
      } else if (typeof errorData.error === 'object') {
        // If error is an object, stringify it
        errorMessage = JSON.stringify(errorData.error);
      } else if (typeof errorData === 'string') {
        errorMessage = errorData;
      }

      // Check if it's a Turnstile error in the message
      if (errorMessage.toLowerCase().includes('turnstile') ||
          errorMessage.toLowerCase().includes('security verification')) {
        throw new TurnstileError(errorMessage);
      }
    } catch (e) {
      // If it's already a TurnstileError, rethrow it
      if (e instanceof TurnstileError) {
        throw e;
      }
      // Failed to parse error response - use response text
      try {
        const text = await response.text();
        if (text) {
          errorMessage = text;
        }
      } catch {
        // Ignore text parsing errors
      }
    }
    throw new APIError(response.status, errorMessage);
  }

  // Success
  try {
    const result = await response.json();
    // Unwrap the data field if present (API returns { success: true, data: {...} })
    if (result.success && result.data) {
      return result.data;
    }
    return result;
  } catch (error) {
    throw new APIError(response.status, 'Failed to parse response');
  }
}

export const api = {
  /**
   * Analyze goals and get personalized questions
   * Turnstile removed - API uses IP rate limiting instead
   */
  async analyzeGoals(
    goals: string[],
    turnstileToken?: string
  ): Promise<AnalyzeGoalsResponse> {
    const response = await fetch(`${API_URL}/productivity/analyze-goals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        goals,
        ...(turnstileToken && { turnstileToken }),
      } as AnalyzeGoalsRequest),
    });

    return handleResponse<AnalyzeGoalsResponse>(response);
  },

  /**
   * Generate complete productivity report
   * Turnstile removed - API uses IP rate limiting instead
   */
  async generateReport(
    goals: string[],
    answers: Record<string, string>,
    email: string,
    consentMarketing: boolean,
    turnstileToken?: string
  ): Promise<GenerateReportResponse> {
    const response = await fetch(`${API_URL}/productivity/generate-report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        goals,
        answers,
        email,
        consentMarketing,
        ...(turnstileToken && { turnstileToken }),
      } as GenerateReportRequest),
    });

    return handleResponse<GenerateReportResponse>(response);
  },

  /**
   * Get existing report by userId
   */
  async getReport(userId: string): Promise<ProductivityReport> {
    const response = await fetch(`${API_URL}/productivity/report/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await handleResponse<{ report: ProductivityReport }>(response);
    // Extract the nested report object
    return data.report;
  },

  /**
   * Convert soft user to full My Best Day account
   * Turnstile removed - API uses IP rate limiting instead
   */
  async convertToFull(
    softUserId: string,
    turnstileToken?: string,
    googleCredential?: string
  ): Promise<ConvertToFullResponse> {
    const response = await fetch(`${API_URL}/productivity/convert-to-full`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        softUserId,
        ...(googleCredential && { googleCredential }),
        ...(turnstileToken && { turnstileToken }),
      } as ConvertToFullRequest),
    });

    return handleResponse<ConvertToFullResponse>(response);
  },
};

export type {
  ProductivityQuestion,
  ProductivityReport,
  AnalyzeGoalsResponse,
  GenerateReportResponse,
  ConvertToFullResponse,
};
